/** TypeScript types for the OMS operator UI (spec §8.1). Mirror of
 *  src/topology_platform/schemas/oms.py — keep in sync.
 *
 *  TRUST + PHI contract (schemas/oms.py module docs):
 *  - List/detail rows carry operator TRUST fields (detection source, confidence,
 *    reportable/confirmed, last-signal time, duration, customers + critical
 *    count) the active-list and detail views render.
 *  - `critical_customers_affected` is PHI-by-inference: it is `null` with
 *    `critical_customers_protected === true` when SUPPRESSED for a viewer below
 *    the count floor. NEVER render a suppressed count as a number — show
 *    "protected". The exact number only arrives for dispatcher+.
 *  - Every list/history/meters/critical endpoint is CURSOR-paginated: the
 *    envelope carries an opaque `next_cursor` (`null` = exhausted). No endpoint
 *    returns an unbounded list.
 *  - Mutations carry `expected_updated_at` (the `updated_at` last read) for
 *    optimistic concurrency; a stale value yields a 409 (see isConflictError). */

import type { GeoJSONFeatureCollection } from './topology.ts'

// Coarse cause codes (spec §8): EQ WX VH AN TR DG OL PL UK, plus PS / PU / HE
// for PowerTRX cause groups (migration 036). Set from the PowerTRX cause
// (`cause_detail`) when one is picked.
export type CauseCode =
  | 'EQ'
  | 'WX'
  | 'VH'
  | 'AN'
  | 'TR'
  | 'DG'
  | 'OL'
  | 'PL'
  | 'PS'
  | 'PU'
  | 'HE'
  | 'UK'

/** `device` = a protective-device parent event (fuse/recloser/switch) pooled
 *  from its member transformers; the engine creates it, an operator cannot
 *  (manual creation stays four-scoped — see OperatorActions' SCOPES). */
export type OutageScope = 'meter' | 'transformer' | 'device' | 'feeder' | 'substation'
export type OutageStatus = 'active' | 'restored' | 'closed' | 'cancelled'

export type OutagePriority = 'critical' | 'high' | 'medium' | 'low'

/** Priority dropdown options (spec §4.4 — an enum, no longer free text). */
export const OUTAGE_PRIORITIES: OutagePriority[] = ['critical', 'high', 'medium', 'low']

export const PRIORITY_LABELS: Record<OutagePriority, string> = {
  critical: 'Critical',
  high: 'High',
  medium: 'Medium',
  low: 'Low',
}

export const PRIORITY_CLASSES: Record<OutagePriority, string> = {
  critical: 'bg-red-100 text-red-800',
  high: 'bg-orange-100 text-orange-800',
  medium: 'bg-yellow-100 text-yellow-800',
  low: 'bg-gray-100 text-gray-600',
}

// =========================================================================
// Dispatch location (spec 2026-09-01 §A) — mirror of schemas/oms.py
// =========================================================================

export type DispatchTarget = 'confirmed_device' | 'suggested_device' | 'event_device'
export type AddressBasis = 'device' | 'nearest_transformer' | 'none'

/** Server-resolved "send the crew here" point. `address_basis` says where the
 *  address came from; `nearest_transformer` is a PROXY (distance ships too).
 *  Meter targets never carry an address. */
export interface DispatchLocation {
  lat: number
  lon: number
  target: DispatchTarget
  device_id: number | null
  device_label: string | null
  device_type: string | null
  pole_id: string | null
  address: string | null
  address_basis: AddressBasis
  address_distance_m: number | null
}

// =========================================================================
// Read models
// =========================================================================

/** One row of the active/history list — includes the operator TRUST fields.
 *  `critical_customers_affected` is suppressed to `null` (with
 *  `critical_customers_protected === true`) for viewers below the count floor;
 *  dispatcher+ always sees the exact value. */
export interface OutageListItem {
  id: number
  scope: OutageScope
  device_type: string
  /** NULL for an UNRESOLVED meter-scope event (no GIS crosswalk) — identity is
   *  then `unresolved_meter_number` (PR #28 finding C / spec §6.5). */
  device_id: number | null
  unresolved_meter_number: string | null
  status: OutageStatus
  // --- trust signals (spec §8 "per-row trust signals") ---
  /** 'lg_sftp' | 'ami_inferred' | 'manual' | 'aggregated' */
  detection_source: string
  /** rolled-up signal confidence (0..1) */
  confidence: number | null
  reportable_for_reliability: boolean
  confirmed_at: string | null
  confirmed_by: string | null
  // --- source-device localization (spec §2.2/§4.2). ENGINE-suggested +
  // OPERATOR-confirmed; NOT the reliability confirmed_* above. ---
  suggested_device_id: number | null
  suggested_device_confidence: number | null
  suggestion_detail: Record<string, unknown> | null
  source_device_confirmed_id: number | null
  is_manual: boolean
  /** most recent OUTAGE/RESTORE event_time */
  last_signal_time: string | null
  start_time: string
  end_time: string | null
  /** end (or now) - start, computed server-side */
  duration_seconds: number | null
  // --- impact ---
  customers_affected: number | null
  /** SUPPRESSED to null (see critical_customers_protected) for viewers. */
  critical_customers_affected: number | null
  /** True => count suppressed, NOT zero. Render "protected", never a number. */
  critical_customers_protected: boolean
  meters_reporting_out: number | null
  meters_total: number | null
  // --- rollup linkage (major-outage visibility) ---
  /** Parent event id (engine rollup or operator merge); null for top-level. */
  parent_event_id: number | null
  /** Only from list(roots_only): active/restored child-event counts by scope,
   *  e.g. {"transformer": 177}. Null when not requested or childless. */
  child_counts: Record<string, number> | null
  /** Server-resolved dispatch point; absent/null on mutation responses. */
  dispatch_location?: DispatchLocation | null
  // --- operator-owned ---
  cause_code: string | null
  /** PowerTRX primary-cause name (GET /oms/causes), exported to PowerTRX. */
  cause_detail?: string | null
  cause_description: string | null
  /** Manual outages only: how it was reported (migration 036). */
  report_source?: ReportSource | null
  priority: string | null
  assigned_crew: string | null
  notes: string | null
  /** operator-owned estimated time of restoration (ISO) — spec §4.4. */
  estimated_restoration_at: string | null
  created_by: string | null
  created_at: string
  updated_at: string
}

/** Detail view — the list-item fields plus the parent linkage. The
 *  affected-meter list is NOT embedded (served paged by /{id}/meters).
 *  `picker_feeder_id` is server-resolved (get_outage): the feeder whose devices
 *  the override picker lists — feeder scope => itself, transformer scope => its
 *  feeder, else null. */
export interface OutageDetail extends OutageListItem {
  picker_feeder_id: number | null
}

/** One affected meter/address row (paged; PII-gated at the router). */
export interface AffectedMeterItem {
  meter_id: number | null
  meter_number: string | null
  transformer_id: number | null
  address: string | null
  /** 'signal' | 'device_scope' */
  impact_basis: string | null
  interval_start: string | null
  interval_end: string | null
}

/** One event-timeline entry from oms_restoration_log. */
export interface RestorationLogItem {
  id: number
  event_id: number
  action: string
  action_time: string
  performed_by: string | null
  notes: string | null
  detail: Record<string, unknown> | null
}

/** One critical-customer PHI detail row (phi_authorized only, audited). */
export interface CriticalCustomerItem {
  id: number
  meter_id: number | null
  transformer_id: number | null
  customer_name: string | null
  address: string | null
  contact_phone: string | null
  equipment_type: string | null
  priority_level: string | null
}

/** One reliability-metrics row (read-only; compute job is a later PR). */
export interface ReliabilityMetricItem {
  id: number
  metric_date: string
  metric_period: string
  scope: string
  feeder_id: number | null
  total_customers: number
  customer_minutes_interrupted: number
  customer_interruptions: number
  saidi_minutes: number | null
  saifi_events: number | null
  caidi_minutes: number | null
  outage_count: number | null
  excludes_planned: boolean
  calculated_at: string
}

/** One unmatched / un-rollupable ingest row (admin report, §5.4). */
export interface UnmatchedSignalItem {
  id: number
  meter_number: string | null
  meter_id: number | null
  source: string
  signal_type: string
  event_time: string
  /** 'unresolved_meter' | 'no_transformer' */
  reason: string
}

/** One distinct unmatched meter number — the GIS crosswalk worklist row. */
export interface UnmatchedNumberSummary {
  meter_number: string
  signal_count: number
  first_seen: string
  last_seen: string
  active_event_id: number | null
}

/** Pull heartbeat / data-as-of freshness for the banner (§8 review #9). */
export interface OmsStatus {
  source: string
  last_run_at: string | null
  last_success_at: string | null
  last_event_time: string | null
  files_seen: number | null
  files_ingested: number | null
  files_quarantined: number | null
  /** last_success_at missing/older than the threshold => STALE warning. */
  stale: boolean
  staleness_threshold_minutes: number
  /** aggregation-pass recency (spec §4.4) — distinct from the ingest pull. */
  aggregation_last_run_at: string | null
  aggregation_last_success_at: string | null
  aggregation_stale: boolean
}

// =========================================================================
// Paginated envelope
// =========================================================================

/** Cursor-paginated envelope. `next_cursor` is opaque; `null` = exhausted. */
export interface Page<T> {
  items: T[]
  next_cursor: string | null
  limit: number
}

// =========================================================================
// Map aggregate features (GET /oms/map/outages)
// =========================================================================

/** Properties on each aggregate outage map feature. Geometry is `null` on the
 *  wire (resolved client-side by scope/device to avoid a meter fan-out). */
export interface OutageMapFeatureProperties {
  id: number
  scope: OutageScope
  /** NULL for an unresolved meter-scope event (see OutageListItem). */
  device_id: number | null
  unresolved_meter_number: string | null
  status: OutageStatus
  customers_affected: number | null
  critical_customers_affected: number | null
  critical_customers_protected: boolean
  detection_source: string
  start_time: string | null
  priority: string | null
  estimated_restoration_at: string | null
  suggested_device_id: number | null
  suggested_device_lon: number | null
  suggested_device_lat: number | null
  source_device_confirmed_id: number | null
  /** Parent event id (stored link); null for top-level. */
  parent_event_id: number | null
  /** True when the parent exists AND is active/restored — drives suggestion-diamond suppression. */
  has_live_parent: boolean
  dispatch_location: DispatchLocation | null
  dispatch_lat: number | null
  dispatch_lon: number | null
  dispatch_target: DispatchTarget | null
  /** Same payload the list row carries — lets the popup title a device-scope
   *  event ("fuse N9373 · phase A") without a detail fetch. */
  suggestion_detail: Record<string, unknown> | null
}

/** GeoJSON aggregate collection from /oms/map/outages (one feature per event).
 *  Feature geometry may be null; feature carries a top-level `id`. */
export type OutageMapFeatureCollection = GeoJSONFeatureCollection

// =========================================================================
// Mutation bodies (operator-owned fields ONLY — §3.5)
// =========================================================================

/** Manual outage create (dispatcher). `device_id` is polymorphic by `scope`. */
export interface OutageCreate {
  scope: OutageScope
  device_id: number
  cause_code?: CauseCode | null
  /** PowerTRX cause name; wins over cause_code (the server derives the code). */
  cause_detail?: string | null
  cause_description?: string | null
  priority?: OutagePriority | null
  assigned_crew?: string | null
  notes?: string | null
  /** defaults to now server-side */
  start_time?: string | null
  /** Already over: recorded closed at this time (call-in / crew reports). */
  end_time?: string | null
  /** Only these meters of the device were out (default: all). */
  meter_ids?: number[] | null
  report_source?: ReportSource | null
  /** Confirm on entry -> reportable for reliability at once. */
  confirmed?: boolean
}

/** Optimistic-concurrency PATCH (dispatcher). Operator-owned fields only.
 *  `expected_updated_at` is the `updated_at` last read; a stale value => 409. */
export interface OutagePatch {
  expected_updated_at: string
  cause_code?: CauseCode | null
  /** PowerTRX cause name, or null to clear; the server sets cause_code from it. */
  cause_detail?: string | null
  cause_description?: string | null
  priority?: OutagePriority | null
  assigned_crew?: string | null
  notes?: string | null
  /** ISO instant, or null to clear (spec §4.4). */
  estimated_restoration_at?: string | null
}

/** Close/cancel/confirm bodies — all optimistic on `expected_updated_at`. */
export interface OutageStateChange {
  expected_updated_at: string
  notes?: string | null
}

export type OutageClose = OutageStateChange
export type OutageCancel = OutageStateChange
export type OutageConfirm = OutageStateChange

/** Merge/roll-up events under a single primary (dispatcher; optimistic). */
export interface OutageMerge {
  primary_id: number
  merge_ids: number[]
  expected_updated_at: string
  notes?: string | null
}

/** Split meters off an event into a new event (dispatcher; optimistic). */
export interface OutageSplit {
  meter_ids: number[]
  expected_updated_at: string
  notes?: string | null
}

/** One protective device on a feeder (the override picker; GET
 *  /oms/feeders/{id}/protective-devices). */
export interface ProtectiveDeviceItem {
  id: number
  name: string | null
  gis_id: string | null
  device_type: string
  feeder_id: number | null
  longitude: number | null
  latitude: number | null
}

/** One other active event converging on this one's device chain (§2.2 step 5). */
export interface ConvergentEventItem {
  id: number
  scope: OutageScope
  device_id: number | null
  suggested_device_id: number | null
  suggested_device_confidence: number | null
  customers_affected: number | null
}

/** One time-correlated event ("likely part of the same outage" hint). */
export interface CorrelatedEventItem {
  id: number
  scope: OutageScope
  device_id: number | null
  unresolved_meter_number: string | null
  start_time: string
  customers_affected: number | null
  seconds_apart: number
}

/** Suggestion-accuracy counts from the audit log (§4.2). */
export interface SourceDeviceMetrics {
  confirmed: number
  overridden: number
  total: number
  agreement_rate: number | null
}

/** Confirm the engine-suggested source device (dispatcher; optimistic). */
export interface SourceDeviceConfirm {
  expected_updated_at: string
  notes?: string | null
}

/** Override the suggestion with an operator-picked device (dispatcher; optimistic). */
export interface SourceDeviceOverride {
  expected_updated_at: string
  device_id: number
  notes?: string | null
}

// =========================================================================
// UI constants
// =========================================================================

/** Color-by-scope for the outage map + list badges (spec §8): Red
 *  feeder/substation, Orange transformer, Yellow meter. */
export const OUTAGE_SCOPE_COLORS: Record<OutageScope, string> = {
  substation: '#e74c3c',
  feeder: '#e74c3c',
  device: '#d35400',
  transformer: '#e67e22',
  meter: '#f1c40f',
}

/** Human-readable labels for the cause-code dropdown (spec §8). */
export const CAUSE_CODE_LABELS: Record<CauseCode, string> = {
  EQ: 'Equipment failure',
  WX: 'Weather',
  VH: 'Vehicle',
  AN: 'Animal',
  TR: 'Tree / vegetation',
  DG: 'Dig-in / excavation',
  OL: 'Overload',
  PL: 'Planned',
  PS: 'Power supply',
  PU: 'Public / third party',
  HE: 'Utility human error',
  UK: 'Unknown',
}

/** How a manually entered outage was reported (migration 036). */
export type ReportSource = 'call_in' | 'crew' | 'scada' | 'other'

export const REPORT_SOURCE_LABELS: Record<ReportSource, string> = {
  call_in: 'Customer call-in',
  crew: 'Crew / field report',
  scada: 'SCADA',
  other: 'Other',
}

// =========================================================================
// PowerTRX Reliability export (EIA-861 Schedule 3 reporting)
// =========================================================================

/** One node of PowerTRX's primary-cause tree, depth-first order. */
export interface CauseOption {
  name: string
  /** ancestors, root first */
  path: string[]
  depth: number
  /** the coarse cause_code it sets */
  code: CauseCode
}

export interface PowertrxReviewItem {
  id: number
  scope: OutageScope
  status: OutageStatus
  start_time: string
  end_time: string | null
  customers_affected: number | null
  location: string
  updated_at: string
  reason: 'needs_cause' | 'still_open'
}

export interface PowertrxPreview {
  start: string
  end: string
  utility_name: string
  default_customers_served: number
  outages: number
  rows: number
  customers_interrupted: number
  customer_minutes: number
  already_exported: number
  cancelled: number
  no_customer_data: number
  review: PowertrxReviewItem[]
}
